import { AlertTriangle, CheckCircle2, Upload, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { apiClient } from "@/lib/api";
import {
  classifyContrast,
  contrastRatio,
  hexToHslTriple,
  hslTripleToHex,
  isHslTriple,
} from "@/lib/brand-color";
import { cn } from "@/lib/utils";
import type { BrandConfig, Uuid } from "@tilt/admin-sdk";

/**
 * Whitelabel brand editor - partner + organization use the same
 * component. Surfaces every field a partner can customise:
 *
 *   - Identity: logo, product name, tagline, login background.
 *   - Typography: body / heading / mono fonts (per theme) + external
 *     font import URL.
 *   - Colors: primary, primary-text, background, foreground, accent
 *     (per theme).
 *   - Footer / legal: copyright, powered-by, T&Cs URL, privacy URL.
 *
 * Per-theme: the colors + fonts section has Light / Dark tabs. Light
 * tab edits root-level keys; Dark edits ``draft.dark.X``. When all
 * dark fields are cleared, the ``dark`` sub-object drops from the
 * payload entirely.
 *
 * Each field surfaces:
 *   - a human-readable help line explaining what the value affects;
 *   - a placeholder showing the value that will actually render if
 *     the field is blank (inherited from the parent → platform default).
 *
 * WCAG contrast: the readability panel computes ratios against the
 * actually-rendered values for both themes. Saving is blocked when any
 * pair drops below 3:1 - text becomes unreadable for cashiers on
 * glared / angled monitors and the running deploy looks broken.
 */

// Platform defaults - what the POS / portal renders when neither the
// partner's brand_config nor any ancestor's set a value. Mirrors the
// CSS-var defaults in tilt-pos/apps/web/src/index.css. ``light`` is the
// :root block; ``dark`` is the html.dark block.
const PLATFORM_DEFAULTS = {
  light: {
    primary_color: "234 89% 54%", // indigo-600
    primary_foreground: "0 0% 100%", // white
    background: "60 9% 98%", // stone-50 (canvas)
    foreground: "24 10% 10%", // stone-900 (text-strong)
    accent: "226 100% 94%", // indigo-50
  },
  dark: {
    primary_color: "217.2 91.2% 70%", // fallback in .dark; 70% lightness for text contrast
    primary_foreground: "24 10% 10%", // near-black; dark text on light primary bg
    background: "24 10% 7%", // stone-950 (canvas)
    foreground: "60 9% 96%", // stone-100
    accent: "24 6% 22%",
  },
} as const;

const COLOR_KEYS = [
  "primary_color",
  "primary_foreground",
  "background",
  "foreground",
  "accent",
] as const;
const FONT_KEYS = ["font_family", "heading_font_family", "mono_font_family"] as const;

type ColorKey = (typeof COLOR_KEYS)[number];
type FontKey = (typeof FONT_KEYS)[number];
type Theme = "light" | "dark";

const COLOR_LABELS: Record<ColorKey, { label: string; help: string }> = {
  primary_color: {
    label: "Primary",
    help: "Active nav, CTAs (Continue / Save), step indicator, price pills.",
  },
  primary_foreground: {
    label: "Primary text",
    help: "Text on top of the Primary color. White or near-white usually wins.",
  },
  background: {
    label: "Background",
    help: "Page canvas behind every screen. Subtle off-white / off-black.",
  },
  foreground: {
    label: "Foreground",
    help: "Body text on the canvas - order details, table rows, descriptions.",
  },
  accent: {
    label: "Accent",
    help: "Secondary surfaces - chips, hover states, soft callouts.",
  },
};

const FONT_LABELS: Record<FontKey, { label: string; help: string; placeholder: string }> = {
  font_family: {
    label: "Body font",
    help: "Page text, form fields, button labels. CSS font-family value (e.g. 'Inter', sans-serif).",
    placeholder: "Inter, sans-serif",
  },
  heading_font_family: {
    label: "Heading font",
    help: "Page titles + section headings (h1–h6). Falls through to the body font when blank.",
    placeholder: "(use body font)",
  },
  mono_font_family: {
    label: "Mono font",
    help: "Tabular numerals - amounts, IDs, VINs. Falls through to JetBrains Mono.",
    placeholder: "JetBrains Mono, monospace",
  },
};

const ALLOWED_MIME: Record<string, "image/png" | "image/jpeg" | "image/svg+xml" | "image/webp"> = {
  "image/png": "image/png",
  "image/jpeg": "image/jpeg",
  "image/svg+xml": "image/svg+xml",
  "image/webp": "image/webp",
};

export interface BrandEditorScope {
  kind: "partner" | "organization";
  id: Uuid;
}

export interface BrandEditorProps {
  scope: BrandEditorScope;
  value: BrandConfig;
  inheritedFrom?: BrandConfig | null;
  inheritedLabel?: string;
  onSave: (next: BrandConfig) => Promise<unknown>;
  disabled?: boolean;
  title?: string;
}

export function BrandEditor(props: BrandEditorProps): JSX.Element {
  const { scope, value, inheritedFrom, inheritedLabel, onSave, disabled, title } = props;
  const [draft, setDraft] = useState<BrandConfig>(value);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [activeTheme, setActiveTheme] = useState<Theme>("light");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(value), [draft, value]);
  const effectiveInherit = inheritedFrom ?? {};

  // Per-theme draft / inherited helpers. Light reads root keys; dark
  // reads ``X.dark.key``. When the dark scope's value is blank, the
  // cascade falls through to the light scope's value.
  const draftForTheme = (theme: Theme): Record<string, unknown> =>
    theme === "light"
      ? (draft as Record<string, unknown>)
      : ((draft.dark ?? {}) as Record<string, unknown>);

  const inheritForTheme = (theme: Theme): Record<string, unknown> =>
    theme === "light"
      ? (effectiveInherit as Record<string, unknown>)
      : ((effectiveInherit.dark ?? {}) as Record<string, unknown>);

  // Effective color value the runtime will render for a given theme +
  // key, walking draft → inherited → platform default. Used by the
  // contrast panel so a blank field shows a meaningful ratio against
  // the platform default rather than a misleading 1:1.
  const effectiveColor = (theme: Theme, key: ColorKey): string => {
    const themeDraft = draftForTheme(theme);
    const themeInherit = inheritForTheme(theme);
    if (typeof themeDraft[key] === "string" && (themeDraft[key] as string).trim()) {
      return themeDraft[key] as string;
    }
    if (typeof themeInherit[key] === "string" && (themeInherit[key] as string).trim()) {
      return themeInherit[key] as string;
    }
    // Dark falls through to light before platform default.
    if (theme === "dark") {
      const lightDraft = draftForTheme("light");
      const lightInherit = inheritForTheme("light");
      if (typeof lightDraft[key] === "string" && (lightDraft[key] as string).trim()) {
        return lightDraft[key] as string;
      }
      if (typeof lightInherit[key] === "string" && (lightInherit[key] as string).trim()) {
        return lightInherit[key] as string;
      }
    }
    return PLATFORM_DEFAULTS[theme][key];
  };

  const ratios = {
    light: {
      primary: contrastRatio(
        effectiveColor("light", "primary_color"),
        effectiveColor("light", "primary_foreground"),
      ),
      surface: contrastRatio(
        effectiveColor("light", "background"),
        effectiveColor("light", "foreground"),
      ),
    },
    dark: {
      primary: contrastRatio(
        effectiveColor("dark", "primary_color"),
        effectiveColor("dark", "primary_foreground"),
      ),
      surface: contrastRatio(
        effectiveColor("dark", "background"),
        effectiveColor("dark", "foreground"),
      ),
    },
  };

  const allRatios = [
    ratios.light.primary,
    ratios.light.surface,
    ratios.dark.primary,
    ratios.dark.surface,
  ] as (number | null)[];

  // Block save when any pair is truly unreadable (< 3:1).
  const blockedByContrast = allRatios.some((r) => r != null && r < 3);

  // Warn (but allow save) when any pair is below WCAG AA 4.5:1 for
  // normal text; cashiers on low-quality or glare-affected monitors
  // will struggle. The per-pair ContrastReadout also shows the tier
  // inline; this banner makes it impossible to miss.
  const warnedByContrast = !blockedByContrast && allRatios.some((r) => r != null && r < 4.5);

  // Setters
  const setRootField = (key: string, v: string): void => {
    const next = { ...draft };
    if (v.trim() === "") delete next[key];
    else next[key] = v;
    setDraft(next);
    setSaved(false);
  };

  const setDarkField = (key: string, v: string): void => {
    const nextDark = { ...((draft.dark ?? {}) as Record<string, unknown>) };
    if (v.trim() === "") delete nextDark[key];
    else nextDark[key] = v;
    const next = { ...draft };
    if (Object.keys(nextDark).length === 0) {
      delete next.dark;
    } else {
      next.dark = nextDark as NonNullable<BrandConfig["dark"]>;
    }
    setDraft(next);
    setSaved(false);
  };

  const setThemeField = (theme: Theme, key: string, v: string): void => {
    if (theme === "light") setRootField(key, v);
    else setDarkField(key, v);
  };

  const submit = async (): Promise<void> => {
    setError(null);
    setSaving(true);
    try {
      // Drop blank string fields before saving so the API / cascade
      // sees "unset" rather than empty string. Dark sub-object goes
      // through the same cleanup recursively.
      const cleaned = cleanBrandConfig(draft);
      await onSave(cleaned);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const revert = (): void => {
    setDraft(value);
    setError(null);
    setSaved(false);
  };

  // --------------------------------------------------------------- //
  // Logo upload flow
  // --------------------------------------------------------------- //
  const handleFile = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setError(null);
    const contentType = ALLOWED_MIME[file.type];
    if (!contentType) {
      setError(`Unsupported file type ${file.type}. Use PNG, JPG, SVG, or WEBP.`);
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError("Logo is larger than 2 MB - compress or use an SVG.");
      return;
    }
    setUploading(true);
    try {
      const { put_url, public_url } = await apiClient.requestBrandLogoUpload({
        scope_kind: scope.kind,
        scope_id: scope.id,
        content_type: contentType,
      });
      const putResp = await fetch(put_url, {
        method: "PUT",
        headers: {
          "content-type": contentType,
          "cache-control": "public, max-age=31536000, immutable",
        },
        body: file,
      });
      if (!putResp.ok) {
        const text = await putResp.text().catch(() => "");
        throw new Error(`S3 upload failed (${putResp.status}) ${text.slice(0, 200)}`);
      }
      setRootField("logo_url", public_url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const currentLogo = (draft.logo_url as string | undefined) ?? "";
  const inheritedLogo = (effectiveInherit.logo_url as string | undefined) ?? "";

  return (
    <div className="space-y-5 rounded-lg border bg-card p-4 sm:p-5">
      <div>
        <h2 className="text-sm font-semibold">{title ?? "Branding"}</h2>
        <p className="text-[0.6875rem] text-muted-foreground">
          {inheritedFrom && Object.keys(effectiveInherit).length > 0
            ? `Empty fields inherit from ${inheritedLabel ?? "the parent"} - the placeholder shows the inherited value.`
            : `No parent branding set yet. Empty fields fall back to the portal defaults shown in each placeholder.`}
        </p>
      </div>

      {/* ---- Identity ---- */}
      <Section title="Identity">
        <div>
          <div className="mb-1 block text-xs font-medium text-muted-foreground">Logo</div>
          <div
            className="flex flex-wrap items-start gap-3"
            onDragOver={(e) => {
              e.preventDefault();
              if (!dragging) setDragging(true);
            }}
            onDragLeave={(e) => {
              if (e.currentTarget === e.target) setDragging(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              const file = e.dataTransfer.files?.[0];
              if (file) void handleFile(file);
            }}
          >
            <div
              className={cn(
                "flex h-20 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/30 transition-colors",
                dragging && "border-primary bg-primary/10 ring-2 ring-primary/40",
              )}
            >
              {currentLogo ? (
                <img
                  src={currentLogo}
                  alt="Logo preview"
                  className="h-full w-full object-contain"
                />
              ) : inheritedLogo ? (
                <div className="flex h-full w-full flex-col items-center justify-center gap-1 p-1">
                  <img
                    src={inheritedLogo}
                    alt="Inherited logo"
                    className="h-10 w-10 object-contain opacity-70"
                  />
                  <span className="text-[0.5625rem] uppercase tracking-wide text-muted-foreground">
                    inherited
                  </span>
                </div>
              ) : (
                <span className="text-[0.6875rem] text-muted-foreground">No logo</span>
              )}
            </div>
            <div className="min-w-0 flex-1 space-y-1.5">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={disabled || saving || uploading}
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 rounded-md border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
                >
                  <Upload className="h-3.5 w-3.5" />
                  {uploading ? "Uploading…" : currentLogo ? "Replace logo" : "Upload logo"}
                </button>
                {currentLogo && (
                  <button
                    type="button"
                    disabled={disabled || saving}
                    onClick={() => setRootField("logo_url", "")}
                    className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted disabled:opacity-50"
                  >
                    <X className="h-3.5 w-3.5" />
                    Clear (inherit)
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                onChange={(e) => {
                  void handleFile(e.target.files?.[0]);
                }}
              />
              <input
                type="url"
                value={currentLogo}
                onChange={(e) => setRootField("logo_url", e.target.value)}
                disabled={disabled || saving || uploading}
                placeholder={inheritedLogo || "paste a URL, or use Upload above"}
                className="w-full rounded-md border bg-background px-3 py-1.5 text-xs"
              />
              <p className="text-[0.625rem] text-muted-foreground">
                PNG / JPG / SVG / WEBP · max 2 MB · drag-and-drop or click Upload · uploaded to S3
                with a 1-year immutable cache.
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <LabeledInput
            label="Product name"
            help="Shown in topbar, login split-screen, and the document title."
            value={(draft.product_name as string | undefined) ?? ""}
            placeholder={
              (effectiveInherit.product_name as string | undefined) || "Finterra (default)"
            }
            disabled={disabled || saving}
            onChange={(v) => setRootField("product_name", v)}
          />
          <LabeledInput
            label="Tagline"
            help="One short sentence under the product name on the login screen. Optional."
            value={(draft.tagline as string | undefined) ?? ""}
            placeholder={(effectiveInherit.tagline as string | undefined) || "(blank - hidden)"}
            disabled={disabled || saving}
            onChange={(v) => setRootField("tagline", v)}
          />
        </div>

        <LabeledInput
          label="Login background image URL"
          help="Full-bleed image rendered behind the login form. HTTPS URL. Falls back to the platform's default split-screen graphic when blank."
          value={(draft.login_background_url as string | undefined) ?? ""}
          placeholder={
            (effectiveInherit.login_background_url as string | undefined) ||
            "https://… (blank - platform default)"
          }
          disabled={disabled || saving}
          onChange={(v) => setRootField("login_background_url", v)}
          type="url"
        />
      </Section>

      {/* ---- Footer / Legal ---- */}
      <Section title="Footer & legal">
        <div className="grid gap-3 sm:grid-cols-2">
          <LabeledInput
            label="Powered by"
            help="Footer attribution line. Closest ancestor wins; defaults to the platform name."
            value={(draft.powered_by_text as string | undefined) ?? ""}
            placeholder={
              (effectiveInherit.powered_by_text as string | undefined) || "Finterra (default)"
            }
            disabled={disabled || saving}
            onChange={(v) => setRootField("powered_by_text", v)}
          />
          <LabeledInput
            label="Copyright text"
            help="Bottom-left of every footer. Defaults to '© {year} {Product name}' when blank."
            value={(draft.copyright_text as string | undefined) ?? ""}
            placeholder={
              (effectiveInherit.copyright_text as string | undefined) || "© 2026 Finterra"
            }
            disabled={disabled || saving}
            onChange={(v) => setRootField("copyright_text", v)}
          />
          <LabeledInput
            label="Terms & Conditions URL"
            help="Footer link. Hidden when blank. HTTPS URL."
            value={(draft.terms_url as string | undefined) ?? ""}
            placeholder={(effectiveInherit.terms_url as string | undefined) || "https://…"}
            disabled={disabled || saving}
            onChange={(v) => setRootField("terms_url", v)}
            type="url"
          />
          <LabeledInput
            label="Privacy Policy URL"
            help="Footer link next to T&Cs. Hidden when blank. HTTPS URL."
            value={(draft.privacy_url as string | undefined) ?? ""}
            placeholder={(effectiveInherit.privacy_url as string | undefined) || "https://…"}
            disabled={disabled || saving}
            onChange={(v) => setRootField("privacy_url", v)}
            type="url"
          />
        </div>
      </Section>

      {/* ---- Per-theme: Typography & Colors ---- */}
      <Section title="Typography & colors">
        <ThemeTabs active={activeTheme} onChange={setActiveTheme} />
        <p className="text-[0.6875rem] text-muted-foreground">
          {activeTheme === "light"
            ? "Light-mode values. Empty dark-mode fields fall through to these."
            : "Dark-mode overrides. Empty fields fall through to the light-mode values above."}
        </p>

        {/* Font import URL - theme-agnostic. Loading the font once
            covers both themes, even if the family names differ. */}
        {activeTheme === "light" && (
          <LabeledInput
            label="Font import URL"
            help="External stylesheet that loads the partner's custom fonts (Google Fonts, Adobe Fonts, partner CDN). Without this, font-family names that aren't system-installed won't render. HTTPS only."
            value={(draft.font_import_url as string | undefined) ?? ""}
            placeholder={
              (effectiveInherit.font_import_url as string | undefined) ||
              "https://fonts.googleapis.com/css2?family=…"
            }
            disabled={disabled || saving}
            onChange={(v) => setRootField("font_import_url", v)}
            type="url"
          />
        )}

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {FONT_KEYS.map((key) => {
            const meta = FONT_LABELS[key];
            return (
              <LabeledInput
                key={`${activeTheme}-${key}`}
                label={meta.label}
                help={meta.help}
                value={(draftForTheme(activeTheme)[key] as string | undefined) ?? ""}
                placeholder={
                  (inheritForTheme(activeTheme)[key] as string | undefined) || meta.placeholder
                }
                disabled={disabled || saving}
                onChange={(v) => setThemeField(activeTheme, key, v)}
              />
            );
          })}
        </div>

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {COLOR_KEYS.map((key) => {
            const meta = COLOR_LABELS[key];
            return (
              <ColorRow
                key={`${activeTheme}-${key}`}
                label={meta.label}
                help={meta.help}
                value={(draftForTheme(activeTheme)[key] as string | undefined) ?? ""}
                inherited={(inheritForTheme(activeTheme)[key] as string | undefined) ?? ""}
                platformDefault={PLATFORM_DEFAULTS[activeTheme][key]}
                disabled={disabled || saving}
                onChange={(v) => setThemeField(activeTheme, key, v)}
              />
            );
          })}
        </div>

        {/* Contrast panel for the active theme. Both themes always
            compute - switching tabs just swaps which previews render. */}
        <div className="mt-3 space-y-2 rounded-md border bg-muted/40 p-3">
          <p className="text-[0.6875rem] font-medium text-muted-foreground">
            Readability check ({activeTheme}) - what cashiers will see
          </p>
          <ContrastReadout
            label="Buttons / nav (Primary text on Primary)"
            preview={{
              fgLabel: "Take Payment",
              fgHsl: effectiveColor(activeTheme, "primary_foreground"),
              bgHsl: effectiveColor(activeTheme, "primary_color"),
            }}
            ratio={ratios[activeTheme].primary}
          />
          <ContrastReadout
            label="Page text (Foreground on Background)"
            preview={{
              fgLabel: "Order details",
              fgHsl: effectiveColor(activeTheme, "foreground"),
              bgHsl: effectiveColor(activeTheme, "background"),
            }}
            ratio={ratios[activeTheme].surface}
          />
        </div>
      </Section>

      {warnedByContrast && (
        <div className="flex items-start gap-2 rounded-md border border-amber-400/50 bg-amber-50 p-3 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <p className="font-semibold">Accessibility warning - below WCAG AA (4.5:1).</p>
            <p className="mt-0.5">
              At least one color pair has a contrast ratio under 4.5:1. This fails WCAG 2.1 SC 1.4.3
              for normal-weight text at the sizes used in the POS (14px / 10.5pt). Cashiers with low
              vision or on glare-affected screens will have difficulty reading. You can still save,
              but consider adjusting. Switch the Light / Dark tab to find the borderline pair.
            </p>
          </div>
        </div>
      )}

      {blockedByContrast && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <p className="font-semibold">Save blocked - contrast below readable floor.</p>
            <p className="mt-0.5 text-foreground/80">
              At least one color pair (light or dark mode) has a contrast ratio under 3:1. Cashiers
              will not be able to read the affected surface. Adjust the colors above (or clear the
              field to inherit the platform default), then save. Switch the Light / Dark tab to find
              the failing pair.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={!dirty || disabled || saving || blockedByContrast}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-40"
        >
          {saving ? "Saving…" : "Save brand"}
        </button>
        <button
          type="button"
          onClick={revert}
          disabled={!dirty || saving}
          className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-40"
        >
          Revert
        </button>
        {saved && <span className="text-xs text-primary">Saved.</span>}
      </div>
    </div>
  );
}

function Section(props: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="space-y-3 rounded-md border border-border/60 bg-background/40 p-3 sm:p-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {props.title}
      </div>
      <div className="space-y-3">{props.children}</div>
    </div>
  );
}

function ThemeTabs(props: { active: Theme; onChange: (t: Theme) => void }): JSX.Element {
  return (
    <div
      role="tablist"
      aria-label="Theme"
      className="inline-flex rounded-lg bg-muted p-1 text-xs font-medium"
    >
      {(["light", "dark"] as const).map((t) => (
        <button
          key={t}
          type="button"
          role="tab"
          aria-selected={props.active === t}
          onClick={() => props.onChange(t)}
          className={cn(
            "rounded-md px-3 py-1.5 transition-colors",
            props.active === t
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {t === "light" ? "Light" : "Dark"}
        </button>
      ))}
    </div>
  );
}

function LabeledInput(props: {
  label: string;
  help?: string;
  value: string;
  placeholder: string;
  disabled?: boolean;
  type?: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <label className="block text-xs">
      <span className="mb-1 block font-medium text-muted-foreground">{props.label}</span>
      <input
        type={props.type ?? "text"}
        value={props.value}
        placeholder={props.placeholder}
        disabled={props.disabled}
        onChange={(e) => props.onChange(e.target.value)}
        className="w-full rounded-md border bg-background px-3 py-2 text-sm"
      />
      {props.help && (
        <p className="mt-1 text-[0.625rem] leading-relaxed text-muted-foreground">{props.help}</p>
      )}
    </label>
  );
}

function ColorRow(props: {
  label: string;
  help?: string;
  value: string; // HSL triple OR hex
  inherited: string;
  platformDefault: string;
  disabled?: boolean;
  onChange: (v: string) => void;
}): JSX.Element {
  const { value, onChange, inherited, platformDefault } = props;
  // The native color picker doubles as both the preview swatch and the
  // editor - no separate preview block. When the field is blank, fall
  // back to the inherited hex (or platform default) so the picker still
  // shows what will be applied.
  const fallbackHsl = inherited && isHslTriple(inherited) ? inherited : platformDefault;
  const displayHex = value
    ? isHslTriple(value)
      ? hslTripleToHex(value)
      : /^#[\da-f]{6}$/i.test(value)
        ? value.toLowerCase()
        : "#000000"
    : hslTripleToHex(fallbackHsl);
  const isInheriting = !value;

  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{props.label}</span>
      <div className="flex items-stretch gap-1.5">
        <input
          type="color"
          value={displayHex}
          onChange={(e) => onChange(hexToHslTriple(e.target.value))}
          disabled={props.disabled}
          className={cn(
            "h-9 w-12 flex-shrink-0 cursor-pointer rounded-md border bg-background",
            isInheriting && "opacity-70",
          )}
          aria-label={`${props.label} color picker`}
          title={isInheriting ? "Inherited / default - pick a color to override" : "Current color"}
        />
        <input
          type="text"
          value={value}
          placeholder={inherited || `${platformDefault} (default)`}
          disabled={props.disabled}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            "w-full rounded-md border bg-background px-2 py-2 font-mono text-xs",
            value && !isHslTriple(value) && !/^#[\da-f]{6}$/i.test(value) && "border-destructive",
          )}
          title='HSL triple like "221 83% 53%" or #hex - leave blank to inherit'
        />
      </div>
      {props.help && (
        <p className="mt-1 text-[0.625rem] leading-relaxed text-muted-foreground">{props.help}</p>
      )}
    </label>
  );
}

function ContrastReadout(props: {
  label: string;
  preview: { fgLabel: string; fgHsl: string; bgHsl: string };
  ratio: number | null;
}): JSX.Element {
  const { label, preview, ratio } = props;
  const tier = ratio != null ? classifyContrast(ratio) : null;
  const bgCss = isHslTriple(preview.bgHsl) ? `hsl(${preview.bgHsl})` : preview.bgHsl;
  const fgCss = isHslTriple(preview.fgHsl) ? `hsl(${preview.fgHsl})` : preview.fgHsl;
  return (
    <div className="flex items-center gap-3">
      <div
        className="flex h-7 min-w-[7rem] items-center justify-center rounded-md px-2 text-xs font-semibold ring-1 ring-inset ring-black/5"
        style={{ backgroundColor: bgCss, color: fgCss }}
      >
        {preview.fgLabel}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[0.6875rem] font-medium text-muted-foreground">{label}</div>
        {ratio != null && tier ? (
          <div
            className={cn(
              "flex items-center gap-1 text-[0.6875rem] font-medium",
              tier === "fail" && "text-destructive",
              tier === "large-only" && "text-amber-700 dark:text-amber-300",
              tier === "aa" && "text-emerald-700 dark:text-emerald-300",
              tier === "aaa" && "text-emerald-700 dark:text-emerald-300",
            )}
          >
            {tier === "fail" ? (
              <AlertTriangle className="h-3 w-3" />
            ) : (
              <CheckCircle2 className="h-3 w-3" />
            )}
            <span>
              Contrast {ratio.toFixed(1)}:1 -{" "}
              {tier === "aaa"
                ? "passes AAA"
                : tier === "aa"
                  ? "passes AA"
                  : tier === "large-only"
                    ? "borderline (large text only)"
                    : "fails - unreadable"}
            </span>
          </div>
        ) : (
          <div className="text-[0.6875rem] text-muted-foreground">
            No reading - invalid color value.
          </div>
        )}
      </div>
    </div>
  );
}

/** Drop empty-string fields and empty ``dark`` sub-objects before save
 *  so the API sees "unset" rather than empty strings.
 */
function cleanBrandConfig(config: BrandConfig): BrandConfig {
  const cleaned: BrandConfig = {};
  for (const [k, v] of Object.entries(config)) {
    if (k === "dark" && v && typeof v === "object") {
      const inner: Record<string, unknown> = {};
      for (const [dk, dv] of Object.entries(v as Record<string, unknown>)) {
        if (typeof dv === "string" && dv.trim() === "") continue;
        if (dv != null) inner[dk] = dv;
      }
      if (Object.keys(inner).length > 0) {
        cleaned.dark = inner as NonNullable<BrandConfig["dark"]>;
      }
      continue;
    }
    if (typeof v === "string" && v.trim() === "") continue;
    cleaned[k] = v;
  }
  return cleaned;
}
